import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useModuleIndex, useScreenIndex } from '../context/ModuleContext';
import { useScreenShow } from '../context/ScreenContext';
import { useModal, useModalType } from '../context/ModalContext';

import './FeedbackWidget.css';

const SESSION_KEY = 'clinical-case-library-feedback-session';

const FALLBACK_SECTION = {
  id: 'page',
  label: 'Page',
  selector: 'body',
};

const SECTION_SELECTORS = [
  { id: 'popup-close-button', label: 'Popup close button', selector: '#comparison .close-btn, #submenu .close-btn' },
  { id: 'popup-comparison-title', label: 'Popup title', selector: '#comparison .comparison-title' },
  { id: 'popup-comparison-left-image', label: 'Popup left image', selector: '#comparison .left-col .img' },
  { id: 'popup-comparison-right-image', label: 'Popup right image', selector: '#comparison .right-col .img' },
  { id: 'popup-comparison-left-copy', label: 'Popup left copy', selector: '#comparison .left-col .img-text, #comparison .mob-text1' },
  { id: 'popup-comparison-right-copy', label: 'Popup right copy', selector: '#comparison .right-col .img-text, #comparison .mob-text2' },
  { id: 'popup-comparison-footnote', label: 'Popup footnote', selector: '#comparison .footnote' },
  { id: 'popup-comparison', label: 'Comparison popup', selector: '#comparison-wrap' },
  { id: 'quiz-question', label: 'Quiz question', selector: '.question' },
  { id: 'quiz-answer', label: 'Quiz answer', selector: '.answer' },
  { id: 'quiz-summary', label: 'Quiz summary', selector: '.summary' },
  { id: 'case-comparison-button', label: 'Comparison button', selector: '.comparison-btn' },
  { id: 'case-scan-image', label: 'Case scan image', selector: '.scan-img-wrap' },
  { id: 'case-title', label: 'Case title', selector: '.slide-title' },
  { id: 'case-navigation', label: 'Case navigation', selector: '.content-head .nav' },
  { id: 'case-data', label: 'Case details', selector: '#slide-data' },
  { id: 'library', label: 'Library screen', selector: '#Library' },
  { id: 'menu', label: 'Menu screen', selector: '#menu' },
];

function clampPercent(value) {
  if (value < 0) {
    return 0;
  }

  if (value > 100) {
    return 100;
  }

  return Number(value.toFixed(3));
}

function getStoredSession() {
  try {
    const stored = window.localStorage.getItem(SESSION_KEY);

    if (!stored) {
      return null;
    }

    const session = JSON.parse(stored);

    if (!session.expiresAt || session.expiresAt < Date.now()) {
      window.localStorage.removeItem(SESSION_KEY);
      return null;
    }

    return session;
  } catch (error) {
    window.localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function findFeedbackSection(target) {
  const explicitSection = target.closest('[data-feedback-id]');

  if (explicitSection) {
    const sectionId = explicitSection.getAttribute('data-feedback-id');

    return {
      element: explicitSection,
      id: sectionId,
      label: explicitSection.getAttribute('data-feedback-label') || sectionId,
      selector: `[data-feedback-id="${sectionId}"]`,
    };
  }

  const matchedSection = SECTION_SELECTORS.find((section) => target.closest(section.selector));

  if (matchedSection) {
    return {
      ...matchedSection,
      element: target.closest(matchedSection.selector),
    };
  }

  return {
    ...FALLBACK_SECTION,
    element: document.body,
  };
}

function getCommentElement(comment) {
  if (comment.selector) {
    const element = document.querySelector(comment.selector);

    if (element) {
      return element;
    }
  }

  const mappedSection = SECTION_SELECTORS.find((section) => section.id === comment.section_id);

  if (mappedSection) {
    return document.querySelector(mappedSection.selector);
  }

  return document.body;
}

function formatDate(value) {
  if (!value) {
    return '';
  }

  return new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const FeedbackWidget = () => {
  const isEnabled = process.env.REACT_APP_FEEDBACK_ENABLED === 'true';
  const moduleIndex = useModuleIndex();
  const screenIndex = useScreenIndex();
  const screenShow = useScreenShow();
  const modalOpen = useModal();
  const modalType = useModalType();

  const [session, setSession] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [pin, setPin] = useState('');
  const [comments, setComments] = useState([]);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [pinError, setPinError] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pinPositions, setPinPositions] = useState({});

  const viewContext = useMemo(() => {
    if (modalOpen) {
      return {
        surface: 'popup',
        modalType: modalType || 'unknown',
        moduleIndex,
        screenIndex,
      };
    }

    if (screenShow) {
      return {
        surface: 'menu',
        modalType: null,
        moduleIndex: null,
        screenIndex: null,
      };
    }

    return {
      surface: 'page',
      modalType: null,
      moduleIndex,
      screenIndex,
    };
  }, [modalOpen, modalType, moduleIndex, screenIndex, screenShow]);

  const visibleComments = useMemo(() => {
    return comments.filter((comment) => {
      if (comment.status === 'addressed' || comment.status === 'rejected') {
        return false;
      }

      if (comment.surface !== viewContext.surface) {
        return false;
      }

      if (viewContext.surface === 'menu') {
        return true;
      }

      if (viewContext.surface === 'popup' && comment.modal_type !== viewContext.modalType) {
        return false;
      }

      return String(comment.module_index) === String(viewContext.moduleIndex) &&
        String(comment.screen_index) === String(viewContext.screenIndex);
    });
  }, [comments, viewContext]);

  const selectedComment = useMemo(() => {
    return visibleComments.find((comment) => comment.id === selectedCommentId) || null;
  }, [selectedCommentId, visibleComments]);

  const callApi = useCallback(async (path, options = {}) => {
    const response = await fetch(path, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.token}`,
        ...(options.headers || {}),
      },
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Feedback request failed.');
    }

    return data;
  }, [session]);

  const refreshComments = useCallback(async () => {
    if (!session) {
      return;
    }

    setFeedbackError('');

    try {
      const data = await callApi('/api/feedback/comments');
      setComments(data.comments || []);
    } catch (error) {
      setFeedbackError(error.message);
    }
  }, [callApi, session]);

  const updatePinPositions = useCallback(() => {
    const nextPositions = {};

    visibleComments.forEach((comment) => {
      const element = getCommentElement(comment);

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();

      if (!rect.width || !rect.height) {
        return;
      }

      nextPositions[comment.id] = {
        left: `${rect.left + (rect.width * Number(comment.x_percent || 0)) / 100}px`,
        top: `${rect.top + (rect.height * Number(comment.y_percent || 0)) / 100}px`,
      };
    });

    setPinPositions(nextPositions);
  }, [visibleComments]);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    setSession(getStoredSession());
  }, [isEnabled]);

  useEffect(() => {
    if (session) {
      refreshComments();
    }
  }, [refreshComments, session]);

  useEffect(() => {
    if (!session) {
      return;
    }

    updatePinPositions();
    const timeoutId = window.setTimeout(updatePinPositions, 250);

    window.addEventListener('resize', updatePinPositions);
    window.addEventListener('scroll', updatePinPositions, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePinPositions);
      window.removeEventListener('scroll', updatePinPositions, true);
    };
  }, [session, updatePinPositions, viewContext]);

  useEffect(() => {
    document.body.classList.toggle('feedback-selecting', isSelecting);

    return () => {
      document.body.classList.remove('feedback-selecting');
    };
  }, [isSelecting]);

  useEffect(() => {
    if (!isSelecting) {
      return;
    }

    const handleFeedbackClick = (event) => {
      if (event.target.closest('[data-feedback-ui]')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const section = findFeedbackSection(event.target);
      const rect = section.element.getBoundingClientRect();
      const xPercent = rect.width ? clampPercent(((event.clientX - rect.left) / rect.width) * 100) : 0;
      const yPercent = rect.height ? clampPercent(((event.clientY - rect.top) / rect.height) * 100) : 0;

      setPendingAnchor({
        ...viewContext,
        pageUrl: window.location.href,
        sectionId: section.id,
        sectionLabel: section.label,
        selector: section.selector,
        xPercent,
        yPercent,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      });
      setCommentText('');
      setIsSelecting(false);
      setPanelOpen(true);
    };

    document.addEventListener('click', handleFeedbackClick, true);

    return () => {
      document.removeEventListener('click', handleFeedbackClick, true);
    };
  }, [isSelecting, viewContext]);

  const handleUnlock = async (event) => {
    event.preventDefault();
    setPinError('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/feedback/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, firstName }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Unable to unlock feedback.');
      }

      window.localStorage.setItem(SESSION_KEY, JSON.stringify(data));
      setSession(data);
      setPin('');
      setFirstName('');
    } catch (error) {
      setPinError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitComment = async (event) => {
    event.preventDefault();

    if (!pendingAnchor || !commentText.trim()) {
      return;
    }

    setIsLoading(true);
    setFeedbackError('');

    try {
      const data = await callApi('/api/feedback/comments', {
        method: 'POST',
        body: JSON.stringify({
          ...pendingAnchor,
          comment: commentText,
        }),
      });
      setComments((currentComments) => [data.comment, ...currentComments]);
      setSelectedCommentId(data.comment.id);
      setPendingAnchor(null);
      setCommentText('');
    } catch (error) {
      setFeedbackError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = () => {
    window.localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setComments([]);
    setIsSelecting(false);
    setPendingAnchor(null);
    setPanelOpen(false);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <div className="feedback-widget" data-feedback-ui="true">
      {session && visibleComments.map((comment, index) => (
        pinPositions[comment.id] ?
          <button
            key={comment.id}
            type="button"
            className={selectedCommentId === comment.id ? 'feedback-pin active' : 'feedback-pin'}
            style={pinPositions[comment.id]}
            onClick={() => {
              setSelectedCommentId(comment.id);
              setPanelOpen(true);
            }}
            aria-label={`Open feedback comment ${index + 1}`}
          >
            {index + 1}
          </button>
          : null
      ))}

      {isSelecting ?
        <div className="feedback-selecting-banner">Click the page or popup section to comment</div>
        : null
      }

      <button
        type="button"
        className="feedback-launcher"
        onClick={() => setPanelOpen((open) => !open)}
      >
        Feedback {session && visibleComments.length ? `(${visibleComments.length})` : ''}
      </button>

      {panelOpen ?
        <div className="feedback-panel">
          <div className="feedback-panel-header">
            <div>
              <h2>Feedback</h2>
              {session ? <p>{session.reviewerName}</p> : <p>Unlock reviewer mode</p>}
            </div>
            <button type="button" className="feedback-icon-button" onClick={() => setPanelOpen(false)}>x</button>
          </div>

          {!session ?
            <form className="feedback-form" onSubmit={handleUnlock}>
              <label>
                PIN
                <input value={pin} onChange={(event) => setPin(event.target.value)} type="password" autoComplete="off" />
              </label>
              <label>
                Preferred first name
                <input value={firstName} onChange={(event) => setFirstName(event.target.value)} type="text" autoComplete="given-name" />
              </label>
              {pinError ? <div className="feedback-error">{pinError}</div> : null}
              <button type="submit" className="feedback-primary" disabled={isLoading || !pin || !firstName.trim()}>
                {isLoading ? 'Unlocking...' : 'Unlock'}
              </button>
            </form>
            :
            <>
              {pendingAnchor ?
                <form className="feedback-form" onSubmit={handleSubmitComment}>
                  <div className="feedback-anchor-summary">
                    <span>{pendingAnchor.surface}</span>
                    <strong>{pendingAnchor.sectionLabel}</strong>
                  </div>
                  <label>
                    Comment
                    <textarea value={commentText} onChange={(event) => setCommentText(event.target.value)} rows="5" autoFocus />
                  </label>
                  {feedbackError ? <div className="feedback-error">{feedbackError}</div> : null}
                  <div className="feedback-actions">
                    <button type="button" className="feedback-secondary" onClick={() => setPendingAnchor(null)}>Cancel</button>
                    <button type="submit" className="feedback-primary" disabled={isLoading || !commentText.trim()}>
                      {isLoading ? 'Saving...' : 'Save comment'}
                    </button>
                  </div>
                </form>
                :
                <div className="feedback-controls">
                  <button type="button" className="feedback-primary" onClick={() => {
                    setIsSelecting(true);
                    setPanelOpen(false);
                  }}>
                    Add comment
                  </button>
                  <button type="button" className="feedback-secondary" onClick={refreshComments}>Refresh</button>
                  <button type="button" className="feedback-text-button" onClick={handleSignOut}>Sign out</button>
                </div>
              }

              {feedbackError && !pendingAnchor ? <div className="feedback-error">{feedbackError}</div> : null}

              {selectedComment ?
                <div className="feedback-selected-comment">
                  <div className="feedback-comment-meta">
                    <strong>{selectedComment.reviewer_name}</strong>
                    <span>{formatDate(selectedComment.created_at)}</span>
                  </div>
                  <p>{selectedComment.comment}</p>
                </div>
                : null
              }

              <div className="feedback-comment-list">
                <h3>Current view</h3>
                {visibleComments.length ? visibleComments.map((comment, index) => (
                  <button
                    type="button"
                    key={comment.id}
                    className={selectedCommentId === comment.id ? 'feedback-comment-row active' : 'feedback-comment-row'}
                    onClick={() => setSelectedCommentId(comment.id)}
                  >
                    <span>{index + 1}. {comment.section_id}</span>
                    <small>{comment.reviewer_name} - {formatDate(comment.created_at)}</small>
                  </button>
                )) : <p className="feedback-empty">No open comments here yet.</p>}
              </div>
            </>
          }
        </div>
        : null
      }
    </div>
  );
};

export default FeedbackWidget;